import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BeakerIcon,
  ChartBarIcon,
  CloudArrowDownIcon,
  CodeBracketIcon,
  MagnifyingGlassCircleIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { PaidToolBadge } from '@/modules/featureBilling';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';
import {
  NAV_TOOLS_MENU,
  TOOL_GROUPS,
  TOOLS_DOCS_HREF,
  getFeaturedTool,
  getToolGroupLabel,
  toolShowsInfoLink,
  type NavToolMenuItem,
  type ToolGroupId,
} from '@/components/NavMenus/navMenuConfig';
import ToolBannerPreview from './ToolBannerPreview';
import ToolFinderPanel from './ToolFinderPanel';
import UpgradePlanBadge from './UpgradePlanBadge';
import {
  buildToolAccessContext,
  getToolAccess,
  isToolCatalogVisible,
  toolAccessNeedsUpgrade,
  type ToolAccessContext,
  type ToolAccessResult,
} from './toolAccess';
import { dismissFeaturedTool, readDismissedFeaturedToolId } from './featuredBannerStorage';
import { readStoredToolFinderRecommendation, resolveToolFinderUserId } from './toolFinderStorage';
import './ToolsLauncher.css';

type FilterId = 'all' | ToolGroupId;

function trackToolsOverview(
  label: string,
  href: string,
  kind:
    | 'info'
    | 'tool'
    | 'docs'
    | 'featured'
    | 'filter'
    | 'search'
    | 'aida_guide'
    | 'featured_dismiss'
    | 'upgrade',
) {
  trackEvent({
    name: 'navigation_click',
    buttonId: `tools_overview_${kind}_${slugifyTrackingValue(href || label)}`,
    label: `${label} — ${kind}`,
    page: 'tools',
    section: 'tools_overview',
    feature: 'navigation',
    eventCategory: 'navigation',
  });
}

function ToolIcon({ tool }: { tool: NavToolMenuItem }) {
  if (tool.iconSrc) {
    return (
      <img
        src={tool.iconSrc}
        alt=""
        data-wide={tool.iconWidth && tool.iconWidth >= 70 ? 'true' : undefined}
      />
    );
  }

  switch (tool.id) {
    case 'geo-browse':
      return <BeakerIcon aria-hidden />;
    case 'advanced-search':
      return <MagnifyingGlassCircleIcon aria-hidden />;
    case 'enrichment':
    case 'gsea-ranked':
      return <ChartBarIcon aria-hidden />;
    case 'download':
      return <CloudArrowDownIcon aria-hidden />;
    case 'ontology-explorer':
      return <ShareIcon aria-hidden />;
    default:
      return <CodeBracketIcon aria-hidden />;
  }
}

function FeaturedBanner({
  tool,
  access,
  onDismiss,
}: {
  tool: NavToolMenuItem;
  access: ToolAccessResult;
  onDismiss: () => void;
}) {
  const feature = tool.feature ?? {
    eyebrow: `Meet ${tool.label}`,
    title: `${tool.label}. Now in DISGENET.`,
    body: tool.description,
    ctaLabel: `Explore ${tool.label}`,
  };
  const needsUpgrade = toolAccessNeedsUpgrade(access.status);
  const ctaTo = needsUpgrade ? access.upgradeHref : tool.showTool === false ? tool.infoHref : tool.toolHref;
  const ctaExternal = !needsUpgrade && tool.toolExternal && tool.showTool !== false;
  const ctaLabel = needsUpgrade
    ? access.status === 'login'
      ? 'Sign in to explore'
      : 'Upgrade to explore'
    : (feature.ctaLabel ?? `Explore ${tool.label}`);

  const ctaClassName = 'tools-page__banner-cta';
  const onCtaClick = () => trackToolsOverview(tool.label, ctaTo, needsUpgrade ? 'upgrade' : 'featured');

  return (
    <section className="tools-page__banner" aria-labelledby="tools-featured-title">
      <button
        type="button"
        className="tools-page__banner-close"
        aria-label={`Dismiss ${tool.label} banner`}
        onClick={() => {
          dismissFeaturedTool(tool.id);
          trackToolsOverview(tool.label, tool.id, 'featured_dismiss');
          onDismiss();
        }}
      >
        <XMarkIcon aria-hidden />
      </button>
      <div className="tools-page__banner-copy">
        <p className="tools-page__banner-eyebrow">
          {feature.eyebrow}
          <span className="tools-page__badge-new">NEW</span>
        </p>
        <h2 id="tools-featured-title" className="tools-page__banner-title">
          {feature.title}
        </h2>
        <p className="tools-page__banner-body">{feature.body}</p>
        {ctaExternal ? (
          <a
            href={ctaTo}
            className={ctaClassName}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCtaClick}
          >
            {ctaLabel}
            <ArrowRightIcon aria-hidden />
          </a>
        ) : (
          <Link to={ctaTo} className={ctaClassName} onClick={onCtaClick}>
            {ctaLabel}
            <ArrowRightIcon aria-hidden />
          </Link>
        )}
      </div>
      <div className="tools-page__banner-visual" aria-hidden>
        <ToolBannerPreview toolId={tool.id} />
      </div>
    </section>
  );
}

function ToolCard({ tool, access }: { tool: NavToolMenuItem; access: ToolAccessResult }) {
  const showInfo = toolShowsInfoLink(tool);
  const showTool = tool.showTool !== false;
  const groupLabel = getToolGroupLabel(tool.group);
  const needsUpgrade = toolAccessNeedsUpgrade(access.status);
  const openHref = needsUpgrade ? access.upgradeHref : tool.toolHref;
  const openExternal = !needsUpgrade && Boolean(tool.toolExternal);
  const openLabel = needsUpgrade ? (access.status === 'login' ? 'Sign in' : 'Upgrade') : 'Open tool';

  return (
    <article className={`tools-page__card${needsUpgrade ? ' tools-page__card--gated' : ''}`}>
      <div className="tools-page__card-top">
        <span className="tools-page__card-icon" aria-hidden>
          <ToolIcon tool={tool} />
        </span>
        <span className="tools-page__card-group">{groupLabel}</span>
      </div>

      <h3 className="tools-page__card-title">
        <span>{tool.label}</span>
        {needsUpgrade && <UpgradePlanBadge />}
        {tool.isPaidTool && <PaidToolBadge />}
        {tool.isNew && <span className="tools-page__badge-new tools-page__badge-new--sm">NEW</span>}
      </h3>
      <p className="tools-page__card-desc">{tool.description}</p>

      <div className="tools-page__card-footer">
        <span className="tools-page__card-tag">{tool.tag}</span>
        <div className="tools-page__card-actions">
          {showInfo && (
            <Link
              to={tool.infoHref}
              className="tools-page__card-action tools-page__card-action--muted"
              onClick={() => trackToolsOverview(tool.label, tool.infoHref, 'info')}
            >
              Info
            </Link>
          )}
          {showTool &&
            (openExternal ? (
              <a
                href={openHref}
                className="tools-page__card-action tools-page__card-action--primary"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackToolsOverview(tool.label, openHref, 'tool')}
              >
                {openLabel}
                <ArrowTopRightOnSquareIcon aria-hidden />
              </a>
            ) : (
              <Link
                to={openHref}
                className="tools-page__card-action tools-page__card-action--primary"
                onClick={() => trackToolsOverview(tool.label, openHref, needsUpgrade ? 'upgrade' : 'tool')}
              >
                {openLabel}
                <ArrowRightIcon aria-hidden />
              </Link>
            ))}
        </div>
      </div>
    </article>
  );
}

function Tools() {
  const { user, isAuthenticated } = userAppContext() ?? {};
  const userRoles: string[] = user?.['cognito:groups'] ?? [];
  const accessCtx: ToolAccessContext = useMemo(
    () => buildToolAccessContext(isAuthenticated, userRoles),
    [isAuthenticated, userRoles],
  );
  const userId = resolveToolFinderUserId(user?.sub);

  const [filter, setFilter] = useState<FilterId>('all');
  const [query, setQuery] = useState('');
  const [finderOpen, setFinderOpen] = useState(() => Boolean(readStoredToolFinderRecommendation(userId)));
  const [dismissedFeaturedId, setDismissedFeaturedId] = useState<string | null>(() =>
    readDismissedFeaturedToolId(),
  );

  useEffect(() => {
    setFinderOpen(Boolean(readStoredToolFinderRecommendation(userId)));
  }, [userId]);

  const visibleTools = useMemo(
    () =>
      TOOL_GROUPS.flatMap(group =>
        NAV_TOOLS_MENU.filter(tool => tool.group === group.id && isToolCatalogVisible(tool, accessCtx)),
      ),
    [accessCtx],
  );

  const featuredTool = useMemo(() => {
    const latest = getFeaturedTool(visibleTools);
    if (!latest) return null;
    if (dismissedFeaturedId === latest.id) return null;
    return latest;
  }, [visibleTools, dismissedFeaturedId]);

  const filteredTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visibleTools.filter(tool => {
      if (filter !== 'all' && tool.group !== filter) return false;
      if (!q) return true;
      return (
        tool.label.toLowerCase().includes(q) ||
        tool.description.toLowerCase().includes(q) ||
        tool.tag.toLowerCase().includes(q) ||
        getToolGroupLabel(tool.group).toLowerCase().includes(q)
      );
    });
  }, [visibleTools, filter, query]);

  const filterCounts = useMemo(() => {
    const counts: Record<FilterId, number> = {
      all: visibleTools.length,
      analysis: 0,
      access: 0,
      integrations: 0,
    };
    for (const tool of visibleTools) counts[tool.group] += 1;
    return counts;
  }, [visibleTools]);

  const handleFindTool = () => {
    trackToolsOverview('Find the right tool', 'tool_finder', 'aida_guide');
    setFinderOpen(true);
  };

  return (
    <div className="tools-page">
      <div className="tools-page__inner">
        <header className="tools-page__header">
          <div className="tools-page__header-main">
            <p className="tools-page__eyebrow">The DISGENET ecosystem</p>
            <h1 className="tools-page__title">API & tools</h1>
            <p className="tools-page__subtitle">
              Your next discovery starts here. Explore tools built for your research.
            </p>
          </div>
          <Link
            to={TOOLS_DOCS_HREF}
            className="tools-page__docs-link"
            onClick={() => trackToolsOverview('Explore documentation', TOOLS_DOCS_HREF, 'docs')}
          >
            Explore documentation
            <ArrowTopRightOnSquareIcon aria-hidden />
          </Link>
        </header>

        {featuredTool && (
          <FeaturedBanner
            tool={featuredTool}
            access={getToolAccess(featuredTool.id, accessCtx)}
            onDismiss={() => setDismissedFeaturedId(featuredTool.id)}
          />
        )}

        <div className="tools-page__toolbar">
          <div className="tools-page__filters" role="tablist" aria-label="Filter tools by group">
            {(
              [
                { id: 'all', label: 'All tools' },
                ...TOOL_GROUPS.map(g => ({ id: g.id as FilterId, label: g.label })),
              ] as { id: FilterId; label: string }[]
            ).map(item => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={filter === item.id}
                className={`tools-page__filter${filter === item.id ? ' is-active' : ''}`}
                onClick={() => {
                  setFilter(item.id);
                  trackToolsOverview(item.label, item.id, 'filter');
                }}
              >
                {item.label}
                <span className="tools-page__filter-count">({filterCounts[item.id]})</span>
              </button>
            ))}
          </div>

          <label className="tools-page__search">
            <MagnifyingGlassIcon aria-hidden />
            <span className="sr-only">Search tools</span>
            <input
              type="search"
              placeholder="Search tools..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onBlur={() => {
                if (query.trim()) trackToolsOverview(query.trim(), 'search', 'search');
              }}
            />
          </label>
        </div>

        <p className="tools-page__count">
          {filteredTools.length} tool{filteredTools.length === 1 ? '' : 's'} to power your research.
        </p>

        <div className="tools-page__grid">
          {filteredTools.map(tool => (
            <ToolCard key={tool.id} tool={tool} access={getToolAccess(tool.id, accessCtx)} />
          ))}
        </div>

        {filteredTools.length === 0 && (
          <p className="tools-page__empty">No tools match your filters. Try another group or search term.</p>
        )}

        <footer className="tools-page__help">
          <div className="tools-page__help-copy">
            <QuestionMarkCircleIcon aria-hidden />
            <span>Not sure where to start?</span>
          </div>
          {!finderOpen && (
            <button type="button" className="tools-page__help-cta" onClick={handleFindTool}>
              Find the right tool for your research
              <ArrowRightIcon aria-hidden />
            </button>
          )}
        </footer>

        <ToolFinderPanel
          open={finderOpen}
          onClose={() => setFinderOpen(false)}
          tools={visibleTools}
          renderIcon={tool => <ToolIcon tool={tool} />}
        />
      </div>
    </div>
  );
}

export default Tools;
